import React, {
  memo,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react"
import CheckoutSuccessPopup from "../../../services/CheckoutSuccessPopup"
import PayPalCardFields from "../PayPalCardFields"
import Loader from "../../../ui/loader"
import moment from "moment"
import PayPalApplePay from "../PayPalApplePay"
import ServiceCardSelector from "../../../services/ServiceCardSelector"
import useManageServices, {
  ServiceType,
} from "../../../services/hooks/useManageServices"
import { Button } from "../../../ui/button"
import { MeetingRequests } from "../../../../api/app.service"
import { PayPalButton } from "../PayPalButton"
import { PayPalGooglePay } from "../PayPalGooglePay"
import { PaymentRequests } from "../../../../api/app.service"
import { ServiceRequests } from "../../../../api/app.service"
import { UserCtx } from "../../../../context/userContext"
import { useToast } from "../../../../hooks/useToast"

interface ApplePayWrapperProps {
  createOrder: (
    data: any,
    source: "paypal" | "card" | "apple_pay" | "google-pay"
  ) => Promise<any>
  onApprove: (
    data: any,
    source: "paypal" | "card" | "apple_pay" | "google-pay"
  ) => Promise<any>
  isSdkReady: boolean
  reactiveOrderTotal?: number
}

export const ApplePayWrapper = memo(function ApplePayWrapper({
  createOrder,
  onApprove,
  isSdkReady,
  reactiveOrderTotal,
}: ApplePayWrapperProps) {
  return (
    <div id="paypal-applepay-container" className="mt-1.5">
      <PayPalApplePay
        createOrder={createOrder}
        onApprove={onApprove}
        isSdkReady={isSdkReady}
        reactiveOrderTotal={reactiveOrderTotal}
      />
    </div>
  )
})

interface Props {
  meetingDetails: {
    startTime: Date
    endTime: Date
  }
  appliedDiscount: {
    amount: number
    promoCode: string
  }
  isTermsAgreed: boolean
  setIsTermsAgreed: React.Dispatch<React.SetStateAction<boolean>>
  availabilities: any
  activeContact: any
  activeService: any
  paymentMethods: any
  selectedPaymentMethod: any
  setSelectedPaymentMethod: any
  merchantId: any
  checkoutSuccessPopup: any
  setCheckoutSuccessPopup: any
  activeContactRef: any
  selectedPlanFromChild: any
  validatePromoCode: any
  createMeeting: any
  discountInstallment: any
  discountedUpFrontAmount: any
  discountedOrderTotal: any
  promoTotal: any
  payInFull: boolean
  applePayTotal: any
}

export const PayPalCheckoutServiceComponent: React.FC<Props> = ({
  meetingDetails,
  appliedDiscount,
  isTermsAgreed,
  setIsTermsAgreed,
  availabilities,
  activeContact,
  activeService,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  merchantId,
  selectedPlanFromChild,
  validatePromoCode,
  createMeeting,
  discountInstallment,
  discountedUpFrontAmount,
  discountedOrderTotal,
  promoTotal,
  payInFull,
  applePayTotal,
}) => {
  const clientID = process.env.REACT_APP_PAYPAL_CLIENT_ID
  const { user } = useContext(UserCtx)
  const { getServiceObject } = useManageServices()
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [isSdkReady, setIsSdkReady] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const [hasMounted, setHasMounted] = useState(false)
  const [sdkLoaded, setSdkLoaded] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState<any>(null)
  const [useSavedCard, setUseSavedCard] = useState(false)
  const [checkoutSuccessPopup, setCheckoutSuccessPopup] = useState(false)
  const [spotsLeft, setSpotsLeft] = useState<number | null>(null)
  const [activeDate, setActiveDate] = useState<Date>(
    moment(new Date()).startOf("day").tz(user?.timezone?.value)?.toDate()
  )
  const [meetings, setMeetings] = useState<any[]>([])
  const [paymentSource, setPaymentSource] = useState<
    "paypal" | "card" | "apple_pay" | "google-pay" | null
  >(null)

  const isApplePayEnabled =
    process.env.REACT_APP_PAYPAL_ENABLE_APPLE_PAY === "true"

  const isGooglePayEnabled =
    process.env.REACT_APP_PAYPAL_ENABLE_GOOGLE_PAY === "true"

  const paypalComponents = [
    "buttons",
    "card-fields",
    isApplePayEnabled && "applepay",
    isGooglePayEnabled && "googlepay",
  ]
    .filter(Boolean)
    .join(",")

  const isSubx = false
  const googlePayEnabled =
    activeService?.payment_term === "full_pay" ||
    activeService?.payment_term === "flexible"
  const payInFullRef = useRef(payInFull)

  const determineServiceType = (service: any): ServiceType => {
    if (activeService?.service_details?.type === "recurring") return "recurring"
    if (activeService?.service_details?.type === "group") return "individual"
    if (activeService?.service_details?.type === "individual")
      return "individual"
    if (activeService?.service_details?.type === "bundle") return "bundle"
    return "consultation"
  }
  const [serviceType, setServiceType] = useState<ServiceType>(
    determineServiceType(activeService)
  )

  const [serviceValues, setServiceValues] = useState<any>({
    availability_rule_id: availabilities
      ? availabilities?.find((rule: any) => rule?.is_default).id
      : "",
    isGroupService: activeService?.is_group_service,
    color: activeService?.service_details?.image?.color,
    title: activeService?.service_details?.title,
    description: "",
    feature1: activeService?.service_details?.details?.features?.feature1,
    feature2: activeService?.service_details?.details?.features?.feature2,
    feature3: activeService?.service_details?.details?.features?.feature3,
    sessionLength: activeService?.service_details?.session?.duration,
    sessionCost: activeService?.service_details?.billing.amount,
    currency: activeService?.service_details?.currency || "USD",
    symbol: activeService?.service_details?.symbol,
    isFree: activeService?.service_details?.isFree,
    allowPromo: activeService?.service_details?.allowPromo,
    package: {
      packageSessions: activeService?.service_details?.session?.count,
      packageBillingType:
        activeService?.service_details?.billing?.packageBillingType !==
        undefined
          ? activeService?.service_details?.billing?.packageBillingType
          : activeService?.service_details?.billing?.unit,
      packageCost: activeService?.service_details?.billing.amount,
      installmentIncrements:
        activeService?.service_details?.billing.installmentIncrements,
      upFrontPercentage:
        activeService?.service_details?.billing?.upFrontPercentage,
    },
    recurring: {
      periodCost: activeService?.service_details?.billing.amount,
      recurringFrequency: activeService?.service_details?.billing?.unit,
      sessionsPerPeriod: activeService?.service_details?.session?.count,
      unlimited: false,
    },
    expiration: {
      expireTime: activeService?.service_details?.expiration.time,
      expireType: activeService?.service_details?.expiration.type,
      expires: activeService?.service_details?.expiration?.expires,
    },
    enabled: activeService?.service_details?.enabled,
    profileServiceVisible:
      activeService?.service_details?.visibility?.profile.service,
    profileCostVisible:
      activeService?.service_details?.visibility?.profile.cost,
    marketplaceServiceVisible:
      activeService?.service_details?.visibility?.marketplace.service,
    marketplaceCostVisible:
      activeService?.service_details?.visibility?.marketplace.service,
    image: {
      exists: activeService?.service_details?.image.exists,
      base64: "",
      base64extension: "",
    },
    group: {
      isFree: activeService?.service_details?.isFree,
      weeklyDayTimes: activeService?.service_details?.group?.weeklyDayTimes,
      maximumClients: activeService?.service_details?.maximumClients,
      groupChat: activeService?.service_details?.groupChat,
    },
    serviceLinkedForms: activeService?.service_details?.serviceLinkedForms,
  })

  const user_id = parseInt(user.user_id)
  const isCheckedRef = useRef(false)
  const activeServiceRef = useRef(activeService)
  const useSavedCardRef = useRef(useSavedCard)
  const appliedDiscountRef = useRef(appliedDiscount)
  const activeContactRef = useRef<any>(null)

  const promoTotalRef = useRef<number>(promoTotal)
  const discountedOrderTotalRef = useRef<number>(discountedOrderTotal)
  const discountedUpFrontAmountRef = useRef<number>(discountedUpFrontAmount)
  const discountInstallmentRef = useRef<number>(discountInstallment)

  useEffect(() => {
    promoTotalRef.current = promoTotal
    discountedOrderTotalRef.current = discountedOrderTotal
    discountedUpFrontAmountRef.current = discountedUpFrontAmount
    discountInstallmentRef.current = discountInstallment
  }, [
    promoTotal,
    discountedOrderTotal,
    discountedUpFrontAmount,
    discountInstallment,
  ])

  useEffect(() => {
    discountedTotalRef.current = discountedOrderTotal
  }, [discountedOrderTotal])

  useEffect(() => {
    activeServiceRef.current = activeService
    setServiceType(activeServiceRef.current.service_details.type)
  }, [activeService])

  useEffect(() => {
    activeContactRef.current = activeContact
  }, [activeContact])

  useEffect(() => {
    appliedDiscountRef.current = appliedDiscount
  }, [appliedDiscount])

  useEffect(() => {
    if (window.paypal) {
      setSdkLoaded(true)
    } else {
      console.log("sdk not loading")
    }
  }, [])
  const serviceValuesRef = useRef(serviceValues)

  const valueRef = useRef(serviceValues.sessionCost)

  useEffect(() => {
    valueRef.current = serviceValues.sessionCost
  }, [serviceValues.sessionCost])

  useEffect(() => {
    serviceValuesRef.current = serviceValues
  }, [serviceValues])

  useEffect(() => {
    useSavedCardRef.current = useSavedCard
  }, [useSavedCard])

  useEffect(() => {
    if (activeService?.service_details?.billing?.amount) {
      const rawAmount = String(activeService.service_details.billing.amount)
      const sanitizedAmount = rawAmount.replace(/[^0-9.]/g, "")
      const parsedAmount = parseFloat(sanitizedAmount)

      if (!isNaN(parsedAmount)) {
        let calculatedPrice = parsedAmount

        if (appliedDiscountRef?.current?.amount) {
          const discount = appliedDiscountRef.current.amount

          if (!isNaN(discount)) {
            calculatedPrice = parsedAmount - discount
          }
        }

        setServiceValues((prevValues: any) => ({
          ...prevValues,
          sessionCost: calculatedPrice,
        }))
      } else {
        console.error("Invalid amount:", rawAmount)
      }
    }
  }, [activeService, appliedDiscountRef, validatePromoCode])

  useEffect(() => {
    if (activeService?.service_details) {
      setServiceValues((prev: any) => ({
        ...prev,
        symbol: activeService.service_details.symbol,
        currency: activeService.service_details.currency,
      }))
    }
  }, [activeService])

  const getGroupServiceEnrollment = async () => {
    try {
      const response = await ServiceRequests.checkGroupServiceEnrollment({
        stripe_product_id: activeService?.stripe_product_id,
      })
      const data = response
      const spotsLeft = data.spots_left
      setSpotsLeft(spotsLeft)
      return spotsLeft
    } catch (error) {
      console.error("Error fetching group service enrollment:", error)
      return null
    }
  }

  const getMeetings = () => {
    if (user) {
      const startOfActiveDate = moment(activeDate)
        .startOf("day")
        .utc()
        .tz(user?.timezone?.value)
        .toISOString()
      const endOfSixMonths = moment(activeDate)
        .add(6, "month")
        .endOf("day")
        .utc()
        .tz(user.timezone.value)
        .toISOString()

      MeetingRequests.getMeetings({
        user_id: parseInt(user.user_id),
        profile_id: user.activeProfileId,
        from_date: startOfActiveDate,
        to_date: endOfSixMonths,
      })
        .then((data) => {
          if (data && Array.isArray(data.meetings)) {
            setMeetings(data.meetings)
          } else {
            console.error("Invalid meetings data format", data)
            setMeetings([])
          }
        })
        .catch((ex) => {
          toast.error(ex.response?.data?.message || "An error occurred")
        })
    }
  }

  useEffect(() => {
    getMeetings()
  }, [])

  useEffect(() => {
    payInFullRef.current = payInFull
  }, [payInFull])

  const toMoney = (val: any) => {
    const num = Number(val)
    if (Number.isNaN(num)) return 0
    return Math.round((num + Number.EPSILON) * 100) / 100
  }

  const buildAppliedDiscounts = () => {
    const allowInstallments =
      activeServiceRef.current?.payment_term === "flexible" ||
      activeServiceRef.current?.payment_term === "installment"

    const isPayingInInstallments = allowInstallments && !payInFullRef.current

    if (isPayingInInstallments) {
      const upFrontPct =
        Number(
          activeServiceRef.current?.service_details?.billing?.upFrontPercentage
        ) || 0

      const orderTotal = toMoney(discountedOrderTotalRef.current || 0)

      const orderTotalUpfront = upFrontPct
        ? toMoney((orderTotal * upFrontPct) / 100)
        : toMoney(discountedUpFrontAmountRef.current || 0)

      return {
        promo_value: toMoney(promoTotalRef.current || 0),
        order_total: orderTotal,
        order_total_upfront: orderTotalUpfront,
        order_total_installment: toMoney(discountInstallmentRef.current || 0),
      }
    }

    const orderTotal = toMoney(discountedOrderTotalRef.current || 0)

    return {
      promo_value: toMoney(promoTotalRef.current || 0),
      order_total: orderTotal,
      order_total_upfront: orderTotal,
      order_total_installment: 0,
    }
  }

  const createOrder = useCallback(
    async (
      data: any,
      source: "paypal" | "card" | "apple_pay" | "google-pay"
    ) => {
      if (activeService?.service_details?.isGroupService) {
        const matchingMeeting = meetings.find(
          (meeting) =>
            meeting.service_details?.title ===
            activeService?.service_details.title
        )

        if (matchingMeeting) {
          alert("You are already enrolled in this service.")
          window.location.href = `${process.env.REACT_APP_BASE_URL}/providers/${activeContact?.endpoint_slug}/services`
          return
        }

        const spotsLeft = await getGroupServiceEnrollment()
        if (spotsLeft !== null && spotsLeft <= 0) {
          alert(
            "This group service is full. Please choose a different service."
          )
          window.location.href = `${process.env.REACT_APP_BASE_URL}/providers/${activeContact?.endpoint_slug}/services`
          return
        }
      }

      const serviceObject = getServiceObject(
        serviceType,
        serviceValuesRef.current
      )

      const service = activeServiceRef.current
      const contactRefs = activeContactRef.current
      const saveCard = isCheckedRef.current
      const useSavedCard = useSavedCardRef.current

      return ServiceRequests.createPurchaseService({
        user_id,
        coach_user_id: contactRefs?.user_id,
        client_profile_id: user.activeProfileId,
        coach_profile_id: contactRefs?.profile_id,
        payment_method_id: useSavedCard ? selectedPaymentMethod?.id : "",
        service: { ...serviceObject },
        stripe_product_id: service.stripe_product_id,
        source: source,
        save_for_future: saveCard,
        user_environment: "",
        applied_discounts: buildAppliedDiscounts(),
      })
        .then((response: any) => {
          return response.data
        })
        .then((order: any) => {
          if (order.id) {
            return order.id
          }
        })
    },
    [useSavedCard, selectedPaymentMethod]
  )

  // Finalize the transaction after payer approval
  const onApprove = useCallback(
    async (
      data: any,
      source: "paypal" | "card" | "apple_pay" | "google-pay"
    ) => {
      setPaymentSource(source)
      const service = activeServiceRef.current
      const getContact = activeContactRef.current
      const promo = appliedDiscountRef.current.promoCode

      try {
        const response = await ServiceRequests.completePurchaseService({
          user_id,
          coach_user_id: getContact?.user_id,
          client_profile_id: user.activeProfileId,
          coach_profile_id: getContact?.profile_id,
          payment_method_id: useSavedCard ? selectedPaymentMethod?.id : "",
          stripe_product_id: service.stripe_product_id,
          source,
          order: data.orderID || data,
          user_environment: "",
          service_payment_id: "",
          applied_discounts: {
            entered_promo_code: promo,
            ...buildAppliedDiscounts(),
          },
          payment_term: service?.payment_term,
        })

        if (meetingDetails?.startTime && meetingDetails?.endTime) {
          await createMeeting(response?.service_usage_id)
        } else if (service?.service_details?.isGroupService) {
          await MeetingRequests.updateGroupMeeting({
            profile_id: user.activeProfileId,
            user_id: parseInt(user.user_id),
            coach_user_id: getContact?.user_id,
            group_service_id: service.group_service_id,
          })
        }
        localStorage.removeItem("meetingToSchedule")
        localStorage.removeItem("coachRedirect")
        localStorage.removeItem("memberRedirect")
        setCheckoutSuccessPopup(true)
      } catch (error: any) {
        console.error("Error in onApprove:", error)
        toast.error(error?.response?.data?.message)
      }
    },
    [useSavedCard, selectedPaymentMethod]
  )

  useEffect(() => {
    isCheckedRef.current = isChecked
  }, [isChecked])

  useEffect(() => {
    if (!clientID || !merchantId) return

    const loadPayPalSDK = () => {
      const existing = document.getElementById(
        "paypal-sdk"
      ) as HTMLScriptElement | null

      if (existing) {
        const url = new URL(existing.src)

        const loadedMerchantId = url.searchParams.get("merchant-id")
        const loadedComponents = url.searchParams.get("components")

        if (
          loadedMerchantId === merchantId &&
          loadedComponents === paypalComponents
        ) {
          setIsSdkReady(true)
          return
        }

        existing.remove()
      }

      const script = document.createElement("script")
      script.id = "paypal-sdk"
      script.src = `https://www.paypal.com/sdk/js?components=${paypalComponents}&client-id=${clientID}&merchant-id=${merchantId}&currency=${serviceValues.currency}&intent=capture&disable-funding=paylater`
      script.async = true

      script.onload = () => {
        setIsSdkReady(true)
      }

      script.onerror = (err) => {
        console.error("Failed to load PayPal SDK:", err)
      }

      document.body.appendChild(script)
    }

    loadPayPalSDK()
  }, [clientID, merchantId, paypalComponents, serviceValues.currency])

  useEffect(() => {
    getPaymentMethods()
  }, [])

  const getPaymentMethods = async () => {
    if (user) {
      try {
        const data = await PaymentRequests.getSavedPaymentMethods({
          user_id: parseInt(user.user_id),
        })
        setPaymentMethods(data?.data?.payment_tokens || [])
        if (data?.payment_methods?.length > 0) {
          setSelectedPaymentMethod(data?.data?.payment_tokens[0].id)
        } else {
          setSelectedPaymentMethod({})
        }
      } catch (ex: any) {
        console.error("Error fetching or updating payment methods:", ex)
      } finally {
      }
    }
  }

  const discountedTotalRef = useRef<number>(discountedOrderTotal)

  useEffect(() => {
    discountedTotalRef.current = discountedOrderTotal
  }, [discountedOrderTotal])

  useEffect(() => {
    if (isSdkReady) {
      setHasMounted(true)
    }
  }, [isSdkReady])

  if (!isSdkReady || !hasMounted) {
    return (
      <div className="justify-center flex items-center">
        <Loader />
      </div>
    )
  }

  const isAPGPmaintenanceInProg =
    process.env.REACT_APP_AP_GP_MAINTENANCE_IN_PROG === "true"

  const isSafari =
    typeof window !== "undefined" &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

  const canShowApplePay =
    isApplePayEnabled &&
    isSafari &&
    typeof (window as any).ApplePaySession !== "undefined"

  const canShowGooglePay = isGooglePayEnabled && payInFull

  const walletLabel = (() => {
    if (canShowApplePay && canShowGooglePay) {
      return (
        <>
          <span className="font-bold">Apple Pay</span> and{" "}
          <span className="font-bold">Google Pay</span>
        </>
      )
    }

    if (canShowApplePay) {
      return <span className="font-bold">Apple Pay</span>
    }

    if (canShowGooglePay) {
      return <span className="font-bold">Google Pay</span>
    }

    return null
  })()

  return (
    <>
      {checkoutSuccessPopup && (
        <CheckoutSuccessPopup
          checkoutSuccessPopup={checkoutSuccessPopup}
          contact={activeContact}
          meetingDetails={meetingDetails}
          activeContact={activeContact}
          activeService={activeService}
          paymentSource={paymentSource}
        />
      )}

      <div className="justify-flex flex-col items-center justify-center">
        <div id="paypal-buttons-container">
          <PayPalButton createOrder={createOrder} onApprove={onApprove} />
        </div>
        {payInFull && (
          <div id="paypal-googlepay-container" className="">
            <PayPalGooglePay
              createOrder={createOrder}
              onApprove={onApprove}
              isSdkReady={isSdkReady}
              enabled={googlePayEnabled}
              orderTotalRef={discountedTotalRef}
            />
          </div>
        )}
        <ApplePayWrapper
          createOrder={createOrder}
          onApprove={onApprove}
          isSdkReady={isSdkReady}
          reactiveOrderTotal={applePayTotal}
        />
        {isAPGPmaintenanceInProg && walletLabel && (
          <div className="w-full max-w-[420px] mb-2 rounded-md bg-[#FF4A00] px-3 py-2 text-center">
            <span className="text-base text-white">
              {walletLabel} {canShowApplePay && canShowGooglePay ? "are" : "is"}{" "}
              currently undergoing maintenance and may be temporarily
              unavailable.
            </span>
          </div>
        )}
        <div className="flex items-center w-full mb-2 mt-2">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="mx-2 text-gray-500 text-graySlate">OR</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>
        {paymentMethods && paymentMethods.length > 0 ? (
          <>
            <ServiceCardSelector
              paymentMethods={paymentMethods}
              selectedPaymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={(paymentMethod: any) => {
                setSelectedPaymentMethod(paymentMethod)
                setUseSavedCard(true)
              }}
              callback={() => {}}
            />
            <div className="mb-4"></div>
            {!useSavedCard ? (
              <PayPalCardFields
                isCheckedRef={isCheckedRef}
                isChecked={isChecked}
                setIsChecked={setIsChecked}
                onApprove={onApprove}
                createOrder={createOrder}
                isTermsAgreed={isTermsAgreed}
                setIsTermsAgreed={setIsTermsAgreed}
                redirectUrl=""
                selectedPlanFromChild={selectedPlanFromChild}
                isSubx={isSubx}
                activeService={activeService}
                payInFull={payInFull}
              />
            ) : (
              <div className="mt-4 text-gray-500">
                <Button
                  type="button"
                  className="w-[95%] text-md ml-2"
                  onClick={() => {
                    setLoading(true)
                    createOrder({}, "card")
                      .then((orderData) => {
                        return onApprove(orderData, "card")
                      })
                      .catch((error) => {
                        console.error(
                          "Error processing saved card payment",
                          error
                        )
                        toast.error(error?.response?.data?.message)
                      })
                      .finally(() => {
                        setLoading(false)
                      })
                  }}
                >
                  {loading ? <Loader /> : "Pay Now"}
                </Button>
              </div>
            )}
          </>
        ) : (
          <PayPalCardFields
            isCheckedRef={isCheckedRef}
            isChecked={isChecked}
            setIsChecked={setIsChecked}
            onApprove={onApprove}
            createOrder={createOrder}
            isTermsAgreed={isTermsAgreed}
            setIsTermsAgreed={setIsTermsAgreed}
            redirectUrl=""
            selectedPlanFromChild={selectedPlanFromChild}
            isSubx={isSubx}
            activeService={activeService}
            payInFull={payInFull}
          />
        )}{" "}
      </div>
    </>
  )
}

export default PayPalCheckoutServiceComponent
